export type ReferenceDetectionStrategy = 'gemini' | 'deterministic';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createDedupedJSONStorage } from '@lib/persistStorage';
import type { GenAILogEntry } from '@domains/google';
import type { ContentType } from '~types/content-analysis';
import type { QuotaLimits, LaneUsage } from '@kernel/quota';

/**
 * GenAI configuration + activity-log store.
 *
 * PERSISTENCE CONTRACT: `partialize` is an explicit ALLOWLIST — settings
 * only. `logs` is an in-memory ring buffer (`maxLogs`-capped) that is NEVER
 * persisted THROUGH THIS STORE: a spread-partialize would write full prompts
 * — book text and base64 table screenshots — to plaintext localStorage
 * unconditionally, with quadratic re-serialization on every set() and latent
 * QuotaExceededError corruption. Entries arrive PRE-REDACTED
 * from the GenAI client (inlineData → {byteCount, hash} — domains/google/
 * genai/logging.ts). persist version 1 strips `logs`/`usageStats` from
 * existing blobs on rehydrate (strip-only, tolerated by older code reading
 * the slimmer blob; localStorage-only — NOT synced user data, so the
 * program's one-in-flight format-change rule is not engaged).
 *
 * Cross-restart log persistence lives OUTSIDE this store: the app-layer
 * mirror (app/google/genaiLogPersistence.ts) writes each appended entry to a
 * small dedicated IndexedDB database and re-injects them at boot via
 * `hydrateLogs` — per-row IDB writes, none of the localStorage failure modes
 * above. The partialize allowlist here still excludes `logs`.
 *
 * `apiKey` stays in the allowlist deliberately (BYO-key product model);
 * secrets isolation (plaintext credentials in localStorage) is a known gap,
 * see D11 in plan/overhaul/analysis/gap-privacy-posture-data-egress-ma.md.
 *
 * The store configures no singleton: the GeminiClient reads config PER CALL
 * via the provider wired in src/app/google/wireGoogle.ts.
 *
 * Quota config: the plain-data quota fields (`quotaLimits`,
 * `bgThrottlePercent`, `fgRpdHeadroom`, `pauseAllGenAI`) ARE in the allowlist
 * — settings the user edits in the GenAI tab to cap request rate/spend.
 * `getQuotaSnapshot` is a live read-back of current usage: an IN-MEMORY
 * provider function injected at startup (wireGoogle installs
 * `() => governor.snapshot()`), so it is NEVER persisted — a function in
 * localStorage would serialize to garbage, the same exclusion as logs above.
 */
interface GenAIState {
  apiKey: string;
  model: string;
  /** Embedding model id, read per embed call by the client. */
  embeddingModel: string;
  /** Requested embedding output dimensionality (vector length). */
  embeddingDims: number;
  /**
   * When ON, the embedding client packs up to 100 texts into one
   * `:batchEmbedContents` call instead of N separate `:embedContent` calls.
   * Default OFF: it is not yet confirmed whether the batch endpoint counts as
   * one request against the daily quota or N, so batching could silently blow
   * the budget — it stays off until measured live. Additive field — tolerated
   * by older persisted blobs (no migration needed).
   */
  useBatchEmbedding: boolean;
  isEnabled: boolean;
  /**
   * Try the free-tier rotation list (GeminiClient.GENAI_ROTATION_MODELS) on a
   * 429 instead of failing on the single configured model. Default ON since
   * persist v5: the Jul–Sep 2026 log export showed rotation active on only 2
   * of 47 days, and every request that hit a spent 20-requests-per-day
   * frontier model with rotation off died without an answer.
   */
  isModelRotationEnabled: boolean;
  isContentAnalysisEnabled: boolean;
  isTableAdaptationEnabled: boolean;
  contentFilterSkipTypes: ContentType[];
  isDebugModeEnabled: boolean;
  referenceDetectionStrategy: ReferenceDetectionStrategy;
  /** In-memory ring buffer (never persisted; pre-redacted entries). */
  logs: GenAILogEntry[];
  maxLogs: number;
  /** Persisted per-lane quota limits, read fresh on every budget reservation. */
  quotaLimits: QuotaLimits;
  /** Persisted per-pool quota limits map. */
  quotaLimitsMap: Record<string, QuotaLimits>;
  /** Persisted fraction (%) of the budget background work may consume. */
  bgThrottlePercent: number;
  /** Persisted RPD headroom reserved for the foreground lane. */
  fgRpdHeadroom: number;
  /** Persisted master pause — zeroes limits so acquire backpressures pre-network. */
  pauseAllGenAI: boolean;
  /**
   * Persisted, default-OFF library-wide opt-in for proactively embedding books.
   * When ON, the FULL TEXT of every book present on this device may be sent to
   * Google for embedding during idle time on the background (low-priority)
   * request lane, so semantic search is ready across the whole library (the
   * foreground indexer covers the currently open book). The single source
   * of truth read by both the consent check and the background backfill task.
   * Additive field — tolerated by older persisted blobs (no migration needed).
   */
  preEmbedLibrary: boolean;
  /**
   * Persisted, default-OFF "Share AI caches across my devices" opt-in.
   * Embeddings are expensive to regenerate (they cost API quota), so a book
   * embedded on one device should be reusable on the user's other devices. When
   * ON, this gates BOTH halves of that: uploading this device's book embeddings
   * into the user's OWN cloud storage, AND reading another device's uploaded
   * embeddings instead of recomputing. A localStorage flag like preEmbedLibrary
   * — no IndexedDB/CRDT change. Additive — tolerated by older persisted blobs
   * (no migration needed). (design: plan/shared-ai-cache-design.md)
   */
  shareAiCaches: boolean;
  /**
   * In-memory injected snapshot provider (the READ mirror of `addLog`):
   * wireGoogle installs `() => governor.snapshot()`. NEVER persisted.
   */
  getQuotaSnapshot?: (ratePool?: string) => Record<'fg' | 'fgd' | 'bg', LaneUsage>;
  /**
   * In-memory injected seam exposing the background lane's budget: wireGoogle
   * installs the background-lane ceiling and the governor's live
   * background-requests-today count, so the background backfill task can check
   * whether it has room to embed more WITHOUT importing the kernel governor.
   * NEVER persisted (same reason as getQuotaSnapshot — a function serializes to
   * garbage).
   */
  getBgQuotaLimits?: () => QuotaLimits;
  getBgUsedRpd?: () => number;
  setApiKey: (key: string) => void;
  setModel: (model: string) => void;
  setEmbeddingModel: (model: string) => void;
  setEmbeddingDims: (dims: number) => void;
  setUseBatchEmbedding: (enabled: boolean) => void;
  setEnabled: (enabled: boolean) => void;
  setModelRotationEnabled: (enabled: boolean) => void;
  setContentAnalysisEnabled: (enabled: boolean) => void;
  setTableAdaptationEnabled: (enabled: boolean) => void;
  setContentFilterSkipTypes: (types: ContentType[]) => void;
  setDebugModeEnabled: (enabled: boolean) => void;
  setReferenceDetectionStrategy: (strategy: ReferenceDetectionStrategy) => void;
  setMaxLogs: (max: number) => void;
  addLog: (log: GenAILogEntry) => void;
  /**
   * Prepend entries restored from the cross-restart IDB mirror (app/google/
   * genaiLogPersistence.ts) — deduped by id against anything already logged
   * this session, capped at maxLogs.
   */
  hydrateLogs: (entries: GenAILogEntry[]) => void;
  clearLogs: () => void;
  setQuotaLimits: (limits: QuotaLimits) => void;
  setQuotaLimitsForPool: (ratePool: string, limits: QuotaLimits) => void;
  resetAllQuotaLimits: () => void;
  setBgThrottlePercent: (percent: number) => void;
  setFgRpdHeadroom: (headroom: number) => void;
  setPauseAllGenAI: (paused: boolean) => void;
  setPreEmbedLibrary: (enabled: boolean) => void;
  setShareAiCaches: (enabled: boolean) => void;
  setQuotaSnapshotProvider: (provider: (ratePool?: string) => Record<'fg' | 'fgd' | 'bg', LaneUsage>) => void;
  /** Install the background-lane budget read-back seam (in-memory; never persisted). */
  setBgBudgetProvider: (getBgQuotaLimits: () => QuotaLimits, getBgUsedRpd: () => number) => void;
}

/** The persisted slice (explicit allowlist — see module header). */
type PersistedGenAIState = Pick<
  GenAIState,
  | 'apiKey'
  | 'model'
  | 'embeddingModel'
  | 'embeddingDims'
  | 'useBatchEmbedding'
  | 'isEnabled'
  | 'isModelRotationEnabled'
  | 'isContentAnalysisEnabled'
  | 'isTableAdaptationEnabled'
  | 'contentFilterSkipTypes'
  | 'isDebugModeEnabled'
  | 'referenceDetectionStrategy'
  | 'maxLogs'
  | 'quotaLimits'
  | 'quotaLimitsMap'
  | 'bgThrottlePercent'
  | 'fgRpdHeadroom'
  | 'pauseAllGenAI'
  | 'preEmbedLibrary'
  | 'shareAiCaches'
>;

/**
 * Sentinel for a budget the provider does NOT meter ("—" or "Unlimited" in the
 * AI Studio rate-limit table): effectively no ceiling, but still a finite number
 * so the governor's arithmetic and the settings meters stay well-defined.
 */
const UNMETERED_REQUESTS = 999_999;
const UNMETERED_TOKENS = 999_999_999;

/**
 * Free-tier quota per rate pool. The pool key IS the model endpoint id the
 * client sends (GeminiClient passes `ratePool: modelId`), so these keys must be
 * the REAL Gemini API model codes — a display name or a family nickname would
 * silently never bind and the pool would fall through to `default`.
 *
 * Numbers mirror the AI Studio rate-limit dashboard (free tier). Three readings
 * of that table map onto the sentinels above:
 *  - a number → that number,
 *  - "—" / "Unlimited" → {@link UNMETERED_REQUESTS} / {@link UNMETERED_TOKENS},
 *  - 0 → a genuine ZERO free-tier allowance. Left as zero deliberately: acquire
 *    then backpressures pre-network with a typed rpm/rpd-exhausted error rather
 *    than spending a round trip to collect a 429.
 */
export const DEFAULT_QUOTA_LIMITS: Record<string, QuotaLimits> = {
  default: { rpm: 100, tpm: 30_000, rpd: 1000 },
  'gemini-1.5-pro': { rpm: 2, tpm: 32_000, rpd: 50 },
  'google-tts': { rpm: 100, tpm: 30_000, rpd: 1000 },
  'openai-tts': { rpm: 100, tpm: 30_000, rpd: 1000 },
  'google-tts-chirp3-hd': { rpm: 100, tpm: 30_000, rpd: 500 },
  'google-tts-wavenet': { rpm: 100, tpm: 100_000, rpd: 2000 },
  'google-tts-studio': { rpm: 100, tpm: 30_000, rpd: 500 },
  'google-tts-standard': { rpm: 100, tpm: 100_000, rpd: 2000 },
  'google-tts-neural2': { rpm: 100, tpm: 30_000, rpd: 500 },
  'google-tts-polyglot': { rpm: 100, tpm: 30_000, rpd: 500 },

  // --- Text-out models ---------------------------------------------------
  // `gemini-flash-lite-latest` is the app's DEFAULT model and a real alias
  // endpoint, so it needs its own pool: without one it would fall through to
  // `default` (100 rpm / 1000 rpd) — ~7x the real Flash-Lite ceiling. Google
  // bills the alias against whichever Flash-Lite it resolves to, and both
  // current Flash-Lite generations carry the same 15/250K/500 free tier, so
  // that is the honest ceiling to hold it to. (The governor keys pools by the
  // literal string sent, so the alias and the concrete model still meter
  // separately here — this caps each at the right ceiling, not their sum.)
  'gemini-flash-lite-latest': { rpm: 15, tpm: 250_000, rpd: 500 },
  'gemini-2.0-flash': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-2.0-flash-lite': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-2.5-flash': { rpm: 5, tpm: 250_000, rpd: 20 },
  'gemini-2.5-flash-lite': { rpm: 10, tpm: 250_000, rpd: 20 },
  'gemini-2.5-pro': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-3-flash-preview': { rpm: 5, tpm: 250_000, rpd: 20 },
  'gemini-3.1-flash-lite': { rpm: 15, tpm: 250_000, rpd: 500 },
  'gemini-3.1-pro-preview': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-3.5-flash': { rpm: 5, tpm: 250_000, rpd: 20 },
  'gemini-3.5-flash-lite': { rpm: 15, tpm: 250_000, rpd: 500 },
  'gemini-3.6-flash': { rpm: 5, tpm: 250_000, rpd: 20 },
  'gemini-3.7-flash': { rpm: 5, tpm: 250_000, rpd: 20 },
  'gemini-3.8-flash': { rpm: 5, tpm: 250_000, rpd: 20 },

  // --- Agents (Interactions API; keyed by their "Agent code") ------------
  'antigravity-preview-05-2026': { rpm: 60, tpm: 100_000, rpd: 100 },
  'deep-research-pro-preview-12-2025': { rpm: 0, tpm: 0, rpd: 0 },

  // --- Multi-modal generative models -------------------------------------
  'gemini-2.5-flash-image': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-2.5-flash-preview-tts': { rpm: 3, tpm: 10_000, rpd: 10 },
  'gemini-2.5-pro-preview-tts': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-3-pro-image': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-3.1-flash-image': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-3.1-flash-lite-image': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-3.1-flash-tts-preview': { rpm: 3, tpm: 10_000, rpd: 10 },
  // The dashboard carries BOTH Omni rows: the model docs now point the
  // "Gemini Omni Flash" name at the 1.1 endpoint, while the original preview
  // endpoint still meters under its own row. Both are a zero free allowance.
  'gemini-omni-flash-preview': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-omni-1.1-flash': { rpm: 0, tpm: 0, rpd: 0 },
  'imagen-4.0-fast-generate-001': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 25 },
  'imagen-4.0-generate-001': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 25 },
  'imagen-4.0-ultra-generate-001': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 25 },
  'lyria-3-clip-preview': { rpm: 0, tpm: 0, rpd: 0 },
  'lyria-3-pro-preview': { rpm: 0, tpm: 0, rpd: 0 },
  // The dashboard's "Veo 3 …" rows are family buckets; the only Veo 3
  // generation still served by the Gemini API is 3.1, so the pools key on the
  // 3.1 endpoints (the 3.0 codes are deprecated and pending shutdown).
  'veo-3.1-fast-generate-preview': { rpm: 0, tpm: UNMETERED_TOKENS, rpd: 0 },
  'veo-3.1-generate-preview': { rpm: 0, tpm: UNMETERED_TOKENS, rpd: 0 },
  'veo-3.1-lite-generate-preview': { rpm: 0, tpm: UNMETERED_TOKENS, rpd: 0 },

  // --- Live API ----------------------------------------------------------
  'gemini-2.5-flash-native-audio-preview-12-2025': {
    rpm: UNMETERED_REQUESTS,
    tpm: 1_000_000,
    rpd: UNMETERED_REQUESTS,
  },
  'gemini-3.1-flash-live-preview': { rpm: UNMETERED_REQUESTS, tpm: 65_000, rpd: UNMETERED_REQUESTS },
  'gemini-3.5-live-translate-preview': {
    rpm: UNMETERED_REQUESTS,
    tpm: 20_000,
    rpd: UNMETERED_REQUESTS,
  },
  // Transcribe ships as a pair of endpoints: the batch one is metered per
  // request, the `-live` session one is unmetered like the rest of Live API.
  'gemini-3.5-transcribe': { rpm: 3, tpm: 10_000, rpd: 25 },
  'gemini-3.5-transcribe-live': {
    rpm: UNMETERED_REQUESTS,
    tpm: 20_000,
    rpd: UNMETERED_REQUESTS,
  },
  'gemini-3.8-live': { rpm: UNMETERED_REQUESTS, tpm: 65_000, rpd: UNMETERED_REQUESTS },
  'gemini-3.8-live-extended-thinking': {
    rpm: UNMETERED_REQUESTS,
    tpm: 65_000,
    rpd: UNMETERED_REQUESTS,
  },

  // --- Other models ------------------------------------------------------
  'gemini-2.5-computer-use-preview-10-2025': { rpm: 0, tpm: 0, rpd: 0 },
  'gemini-embedding-001': { rpm: 100, tpm: 30_000, rpd: 1000 },
  'gemini-embedding-2': { rpm: 100, tpm: 30_000, rpd: 1000 },
  'gemini-robotics-er-1.5-preview': { rpm: 10, tpm: 250_000, rpd: 20 },
  'gemini-robotics-er-1.6-preview': { rpm: 5, tpm: 250_000, rpd: 20 },
  'gemini-robotics-er-2-preview': { rpm: 5, tpm: 250_000, rpd: 20 },
  'gemma-4-26b-a4b-it': { rpm: 30, tpm: 16_000, rpd: 14_400 },
  'gemma-4-31b-it': { rpm: 30, tpm: 16_000, rpd: 14_400 },

  // --- Tool-usage pools (separate AI Studio tables, not the model table) --
  'deep-research-pro-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-2-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-2.0-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'computer-use-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-2.5-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-2.5-flash-lite-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-2.5-pro-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'gemini-3-flash-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'gemini-3.1-pro-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'gemini-3.1-flash-lite-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-3.1-flash-tts-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-3.5-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'gemini-3.5-flash-lite-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-3.5-transcribe-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-3.6-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'gemini-3.7-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'gemini-3.8-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'gemini-robotics-er-1.6-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-robotics-er-2-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
  'gemini-2-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
  'gemini-2.0-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
  'gemini-2.5-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
  'gemini-3-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 0 },
  'default-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
};

/**
 * v3→v4 re-keying: legacy pool key → the REAL model endpoint id. The old keys
 * were display-name-ish slugs that never matched what the client actually sends
 * as `ratePool`, so those pools silently fell through to `default` instead of
 * enforcing the model's own free-tier ceiling.
 */
const V4_POOL_RENAMES: Record<string, string> = {
  'gemini-2.5-flash-tts': 'gemini-2.5-flash-preview-tts',
  'gemini-3.1-flash-tts': 'gemini-3.1-flash-tts-preview',
  // The Gemini 3 family's Live model shipped as 3.1; there is no
  // `gemini-3-flash-live` endpoint (the dashboard row is a family bucket).
  'gemini-3-flash-live': 'gemini-3.1-flash-live-preview',
  'gemini-2.5-flash-native-audio-dialog': 'gemini-2.5-flash-native-audio-preview-12-2025',
  'gemini-3.5-live-translate': 'gemini-3.5-live-translate-preview',
  'gemma-4-26b': 'gemma-4-26b-a4b-it',
  'gemma-4-31b': 'gemma-4-31b-it',
  'imagen-4-fast-generate': 'imagen-4.0-fast-generate-001',
  'imagen-4-generate': 'imagen-4.0-generate-001',
  'imagen-4-ultra-generate': 'imagen-4.0-ultra-generate-001',
};

/**
 * The v3 defaults for the only pools whose NUMBERS (not just their key) changed
 * in v4. A persisted value equal to one of these is an untouched old default and
 * is refreshed to the new figure; anything else is a deliberate user edit and is
 * carried across the rename untouched.
 */
const V3_SUPERSEDED_DEFAULTS: Record<string, QuotaLimits> = {
  'gemma-4-26b': { rpm: 15, tpm: 999_999_999, rpd: 1500 },
  'gemma-4-31b': { rpm: 15, tpm: 999_999_999, rpd: 1500 },
};

const sameLimits = (a: QuotaLimits, b: QuotaLimits): boolean =>
  a.rpm === b.rpm && a.tpm === b.tpm && a.rpd === b.rpd;

export const useGenAIStore = create<GenAIState>()(
  persist(
    (set) => ({
      apiKey: '',
      model: 'gemini-flash-lite-latest',
      embeddingModel: 'gemini-embedding-2',
      embeddingDims: 768,
      useBatchEmbedding: false,
      isEnabled: false,
      isModelRotationEnabled: true,
      isContentAnalysisEnabled: false,
      isTableAdaptationEnabled: false,
      contentFilterSkipTypes: ['reference'],
      isDebugModeEnabled: false,
      referenceDetectionStrategy: 'gemini' as ReferenceDetectionStrategy,
      logs: [],
      maxLogs: 500,
      quotaLimits: { rpm: 100, tpm: 30_000, rpd: 1000 },
      quotaLimitsMap: DEFAULT_QUOTA_LIMITS,
      bgThrottlePercent: 50,
      fgRpdHeadroom: 0,
      pauseAllGenAI: false,
      preEmbedLibrary: false,
      shareAiCaches: false,
      getQuotaSnapshot: undefined,
      getBgQuotaLimits: undefined,
      getBgUsedRpd: undefined,
      setApiKey: (key) => set({ apiKey: key }),
      setModel: (model) => set({ model }),
      setEmbeddingModel: (model) => set({ embeddingModel: model }),
      setEmbeddingDims: (dims) => set({ embeddingDims: dims }),
      setUseBatchEmbedding: (enabled) => set({ useBatchEmbedding: enabled }),
      setEnabled: (enabled) => set({ isEnabled: enabled }),
      setModelRotationEnabled: (enabled) => set({ isModelRotationEnabled: enabled }),
      setContentAnalysisEnabled: (enabled) => set({ isContentAnalysisEnabled: enabled }),
      setTableAdaptationEnabled: (enabled) => set({ isTableAdaptationEnabled: enabled }),
      setContentFilterSkipTypes: (types) => set({ contentFilterSkipTypes: types }),
      setDebugModeEnabled: (enabled) => set({ isDebugModeEnabled: enabled }),
      setReferenceDetectionStrategy: (strategy) =>
        set({ referenceDetectionStrategy: strategy }),
      setMaxLogs: (max) => set({ maxLogs: max }),
      addLog: (log) =>
        set((state) => {
          const newLogs = [...state.logs, log];
          if (newLogs.length > state.maxLogs) {
            newLogs.splice(0, newLogs.length - state.maxLogs);
          }
          return { logs: newLogs };
        }),
      hydrateLogs: (entries) =>
        set((state) => {
          const existing = new Set(state.logs.map((l) => l.id));
          const restored = entries.filter((e) => !existing.has(e.id));
          if (restored.length === 0) return {};
          const merged = [...restored, ...state.logs];
          if (merged.length > state.maxLogs) {
            merged.splice(0, merged.length - state.maxLogs);
          }
          return { logs: merged };
        }),
      clearLogs: () => set({ logs: [] }),
      setQuotaLimits: (limits) =>
        set((state) => ({
          quotaLimits: limits,
          quotaLimitsMap: { ...state.quotaLimitsMap, default: limits },
        })),
      setQuotaLimitsForPool: (ratePool, limits) =>
        set((state) => ({
          quotaLimitsMap: { ...state.quotaLimitsMap, [ratePool]: limits },
          ...(ratePool === 'default' ? { quotaLimits: limits } : {}),
        })),
      resetAllQuotaLimits: () =>
        set({
          quotaLimits: DEFAULT_QUOTA_LIMITS.default,
          quotaLimitsMap: DEFAULT_QUOTA_LIMITS,
        }),
      setBgThrottlePercent: (percent) => set({ bgThrottlePercent: percent }),
      setFgRpdHeadroom: (headroom) => set({ fgRpdHeadroom: headroom }),
      setPauseAllGenAI: (paused) => set({ pauseAllGenAI: paused }),
      setPreEmbedLibrary: (enabled) => set({ preEmbedLibrary: enabled }),
      setShareAiCaches: (enabled) => set({ shareAiCaches: enabled }),
      setQuotaSnapshotProvider: (provider) => set({ getQuotaSnapshot: provider }),
      setBgBudgetProvider: (getBgQuotaLimits, getBgUsedRpd) =>
        set({ getBgQuotaLimits, getBgUsedRpd }),
    }),
    {
      name: 'genai-storage',
      version: 5,
      // Deduped: `logs` is an in-memory ring buffer OUTSIDE the allowlist
      // below, but persist re-serializes and re-writes the whole settings blob
      // after every set — so without dedup each log line (GenAI request,
      // embedding request, TTS worker log) would cost a JSON.stringify of ~20
      // keys plus a synchronous localStorage commit of an unchanged payload.
      storage: createDedupedJSONStorage<PersistedGenAIState>(),
      partialize: (state): PersistedGenAIState => ({
        apiKey: state.apiKey,
        model: state.model,
        embeddingModel: state.embeddingModel,
        embeddingDims: state.embeddingDims,
        useBatchEmbedding: state.useBatchEmbedding,
        isEnabled: state.isEnabled,
        isModelRotationEnabled: state.isModelRotationEnabled,
        isContentAnalysisEnabled: state.isContentAnalysisEnabled,
        isTableAdaptationEnabled: state.isTableAdaptationEnabled,
        contentFilterSkipTypes: state.contentFilterSkipTypes,
        isDebugModeEnabled: state.isDebugModeEnabled,
        referenceDetectionStrategy: state.referenceDetectionStrategy,
        maxLogs: state.maxLogs,
        quotaLimits: state.quotaLimits,
        quotaLimitsMap: state.quotaLimitsMap,
        bgThrottlePercent: state.bgThrottlePercent,
        fgRpdHeadroom: state.fgRpdHeadroom,
        pauseAllGenAI: state.pauseAllGenAI,
        preEmbedLibrary: state.preEmbedLibrary,
        shareAiCaches: state.shareAiCaches,
      }),
      /**
       * v0 → v1: strip the legacy persisted `logs` (full prompts, base64
       * table images) and dead `usageStats` from existing blobs. Settings
       * (apiKey/model/flags) survive untouched — pinned by the captured-
       * blob regression test (useGenAIStore.migration.test.ts).
       *
       * v1 → v2: switch the embedding model off the retired `gemini-embedding-001`
       * onto `gemini-embedding-2` (the GA model — 8192-token inputs vs 2048,
       * prompt-instruction profiles instead of `taskType`, auto-normalized
       * truncated dims). Only the OLD default is flipped, so a hand-picked model
       * is left alone. The two embedding spaces are incompatible, so the prior
       * `-001` vectors are ABANDONED automatically: the stamp guards re-embed the
       * book on the next index pass and skip the stale vectors (regex-only) until
       * then — no purge needed (see EmbeddingIndexer / semanticRank stamp checks).
       *
       * v2 → v3: seed default limits for newly supported Google TTS pools
       * (chirp3-hd, wavenet, studio, standard, neural2, polyglot) so that
       * they can be configured individually.
       *
       * v3 → v4: re-key the Gemini pools onto their REAL API endpoint ids
       * ({@link V4_POOL_RENAMES}) and seed every model now listed in the
       * free-tier table. Pool keys are matched against the model id the client
       * sends, so a slug like `gemma-4-26b` or `imagen-4-generate` never bound
       * to anything — those pools were dead entries in the settings UI while
       * the real request fell through to the far looser `default` pool. User
       * edits survive: a persisted value is carried across the rename as-is,
       * and only a value still equal to its superseded v3 default
       * ({@link V3_SUPERSEDED_DEFAULTS}) is refreshed to the new figure.
       *
       * v4 → v5: model rotation ON becomes the default. The stored `false`
       * of a pre-v5 blob is the OLD default — the settings UI never asked
       * the user to choose it — so it is flipped once; a user who switches
       * rotation off again on a v5 blob keeps it off.
       */
      migrate: (persistedState, version) => {
        if (persistedState && typeof persistedState === 'object') {
          const state = persistedState as Record<string, unknown>;
          if (version < 1) {
            delete state.logs;
            delete state.usageStats;
          }
          if (version < 2 && state.embeddingModel === 'gemini-embedding-001') {
            state.embeddingModel = 'gemini-embedding-2';
          }
        }
        if (version < 5 && persistedState && typeof persistedState === 'object') {
          (persistedState as Record<string, unknown>).isModelRotationEnabled = true;
        }
        if (version < 2 && persistedState && typeof persistedState === 'object') {
          const state = persistedState as Record<string, unknown>;
          const defaultLimits = (state.quotaLimits as QuotaLimits | undefined) || { rpm: 100, tpm: 30_000, rpd: 1000 };
          state.quotaLimitsMap = {
            default: defaultLimits,
            'gemini-1.5-pro': { rpm: 2, tpm: 32_000, rpd: 50 },
            'google-tts': { rpm: 100, tpm: 30_000, rpd: 1000 },
            'openai-tts': { rpm: 100, tpm: 30_000, rpd: 1000 },
            'gemini-embedding-001': { rpm: 100, tpm: 30_000, rpd: 1000 },
            'gemini-2.5-flash': { rpm: 5, tpm: 250_000, rpd: 20 },
            'gemini-2.5-flash-lite': { rpm: 10, tpm: 250_000, rpd: 20 },
            'gemini-2.5-flash-tts': { rpm: 3, tpm: 10_000, rpd: 10 },
            'gemini-3-flash-preview': { rpm: 5, tpm: 250_000, rpd: 20 },
            'gemini-3.1-flash-lite': { rpm: 15, tpm: 250_000, rpd: 500 },
            'gemini-3.1-flash-tts': { rpm: 3, tpm: 10_000, rpd: 10 },
            'gemini-3.5-flash': { rpm: 5, tpm: 250_000, rpd: 20 },
            'gemini-embedding-2': { rpm: 100, tpm: 30_000, rpd: 1000 },
            'gemini-robotics-er-1.5-preview': { rpm: 10, tpm: 250_000, rpd: 20 },
            'gemini-robotics-er-1.6-preview': { rpm: 5, tpm: 250_000, rpd: 20 },
            'gemma-4-26b': { rpm: 15, tpm: 999_999_999, rpd: 1500 },
            'gemma-4-31b': { rpm: 15, tpm: 999_999_999, rpd: 1500 },
            'imagen-4-fast-generate': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 25 },
            'imagen-4-generate': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 25 },
            'imagen-4-ultra-generate': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 25 },
            'gemini-2.5-flash-native-audio-dialog': { rpm: 999_999, tpm: 1_000_000, rpd: 999_999 },
            'gemini-3-flash-live': { rpm: 999_999, tpm: 65_000, rpd: 999_999 },
            'gemini-3.5-live-translate': { rpm: 999_999, tpm: 20_000, rpd: 999_999 },
            'deep-research-pro-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-2-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-2.0-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'computer-use-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-2.5-flash-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-2.5-flash-lite-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-3.1-flash-lite-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-3.1-flash-tts-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-robotics-er-1.6-preview-map-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 500 },
            'gemini-2-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
            'gemini-2.0-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
            'gemini-2.5-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
            'default-search-grounding': { rpm: UNMETERED_REQUESTS, tpm: UNMETERED_TOKENS, rpd: 1500 },
          };
          state.quotaLimits = defaultLimits;
        }
        if (version < 3 && persistedState && typeof persistedState === 'object') {
          const state = persistedState as Record<string, unknown>;
          const map = (state.quotaLimitsMap as Record<string, QuotaLimits> | undefined) || {};
          state.quotaLimitsMap = {
            ...map,
            'google-tts-chirp3-hd': { rpm: 100, tpm: 30_000, rpd: 500 },
            'google-tts-wavenet': { rpm: 100, tpm: 100_000, rpd: 2000 },
            'google-tts-studio': { rpm: 100, tpm: 30_000, rpd: 500 },
            'google-tts-standard': { rpm: 100, tpm: 100_000, rpd: 2000 },
            'google-tts-neural2': { rpm: 100, tpm: 30_000, rpd: 500 },
            'google-tts-polyglot': { rpm: 100, tpm: 30_000, rpd: 500 },
          };
        }
        if (version < 4 && persistedState && typeof persistedState === 'object') {
          const state = persistedState as Record<string, unknown>;
          const map = { ...((state.quotaLimitsMap as Record<string, QuotaLimits> | undefined) || {}) };
          for (const [legacyKey, endpointKey] of Object.entries(V4_POOL_RENAMES)) {
            const persisted = map[legacyKey];
            if (!persisted) continue;
            delete map[legacyKey];
            // An explicit entry already under the endpoint key wins — it is the
            // newer of the two and re-keying must never clobber it.
            if (map[endpointKey]) continue;
            const superseded = V3_SUPERSEDED_DEFAULTS[legacyKey];
            const refreshed = superseded && sameLimits(persisted, superseded);
            if (!refreshed) map[endpointKey] = persisted;
          }
          // Seed pools this blob has never seen (new models, re-keyed pools
          // whose stale numbers were dropped above) without touching any
          // limit the user already has.
          state.quotaLimitsMap = { ...DEFAULT_QUOTA_LIMITS, ...map };
        }
        return persistedState as PersistedGenAIState;
      },
    },
  ),
);
